const CURATED_TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Phoenix', label: 'Arizona Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Halifax', label: 'Atlantic Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Europe/London', label: 'United Kingdom Time' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Asia/Dubai', label: 'Gulf Time' },
  { value: 'Asia/Kolkata', label: 'India Time' },
  { value: 'Asia/Singapore', label: 'Singapore Time' },
  { value: 'Asia/Tokyo', label: 'Japan Time' },
  { value: 'Australia/Sydney', label: 'Eastern Australia Time' },
  { value: 'UTC', label: 'UTC' },
];
const SCHEDULE_DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

function supportsTimezone(value) {
  const timezone = String(value || '').trim();
  if (!timezone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function getSupportedTimezoneSet() {
  try {
    return new Set(Intl.supportedValuesOf('timeZone'));
  } catch {
    return null;
  }
}

function formatCityFallback(timezone) {
  const parts = String(timezone || '').split('/');
  const tail = parts[parts.length - 1] || 'UTC';
  return tail.replace(/_/g, ' ');
}

function getUtcPartsForTimestamp(timestampMs, timezone) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(new Date(timestampMs));
  const map = {};
  parts.forEach((part) => {
    if (part.type !== 'literal') map[part.type] = part.value;
  });
  return {
    year: Number(map.year || 0),
    month: Number(map.month || 0),
    day: Number(map.day || 0),
    hour: Number(map.hour || 0),
    minute: Number(map.minute || 0),
    second: Number(map.second || 0),
  };
}

function getTimezoneOffsetMillis(timestampMs, timezone) {
  const parts = getUtcPartsForTimestamp(timestampMs, timezone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, 0);
  return asUtc - timestampMs;
}

export function getBrowserTimezone(fallback = 'UTC') {
  try {
    const timezone = String(Intl.DateTimeFormat().resolvedOptions().timeZone || '').trim();
    return supportsTimezone(timezone) ? timezone : fallback;
  } catch {
    return fallback;
  }
}

export function normalizeTimezone(value, fallback = 'UTC') {
  const timezone = String(value || '').trim();
  if (supportsTimezone(timezone)) return timezone;
  return supportsTimezone(fallback) ? fallback : 'UTC';
}

export function formatTimezoneOffsetLabel(timezone, date = new Date()) {
  const resolvedTimezone = normalizeTimezone(timezone, 'UTC');
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: resolvedTimezone,
      timeZoneName: 'longOffset',
      hour: '2-digit',
    }).formatToParts(date);
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value || 'UTC';
    return offset.replace('GMT', 'UTC');
  } catch {
    return 'UTC';
  }
}

export function parseScheduledDateTimeInTimezone(input, timezone) {
  const value = String(input || '').trim();
  const resolvedTimezone = normalizeTimezone(timezone, 'UTC');
  if (!SCHEDULE_DATE_TIME_RE.test(value)) return null;
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map((entry) => Number(entry));
  const [hour, minute] = timePart.split(':').map((entry) => Number(entry));
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const firstOffset = getTimezoneOffsetMillis(utcGuess, resolvedTimezone);
  let resolvedMs = utcGuess - firstOffset;
  const secondOffset = getTimezoneOffsetMillis(resolvedMs, resolvedTimezone);
  if (secondOffset !== firstOffset) resolvedMs = utcGuess - secondOffset;
  const resolvedParts = getUtcPartsForTimestamp(resolvedMs, resolvedTimezone);
  if (
    resolvedParts.year !== year
    || resolvedParts.month !== month
    || resolvedParts.day !== day
    || resolvedParts.hour !== hour
    || resolvedParts.minute !== minute
  ) {
    return null;
  }
  return new Date(resolvedMs);
}

export function timezoneLabel(timezone, date = new Date()) {
  const resolvedTimezone = normalizeTimezone(timezone, 'UTC');
  const curated = CURATED_TIMEZONES.find((entry) => entry.value === resolvedTimezone);
  const baseLabel = curated?.label || formatCityFallback(resolvedTimezone);
  const offsetLabel = formatTimezoneOffsetLabel(resolvedTimezone, date);
  return baseLabel === 'UTC' ? 'UTC' : `${baseLabel} (${offsetLabel})`;
}

export function buildTimezoneOptions({ selectedTimezone = '', fallbackTimezone = '' } = {}) {
  const supportedSet = getSupportedTimezoneSet();
  const values = [
    selectedTimezone,
    fallbackTimezone,
    ...CURATED_TIMEZONES.map((entry) => entry.value),
  ].filter(Boolean);

  return values
    .filter((value, index, list) => list.indexOf(value) === index)
    .filter((value) => supportsTimezone(value) && (!supportedSet || supportedSet.has(value) || value === 'UTC'))
    .map((value) => ({
      value,
      label: timezoneLabel(value),
    }));
}

export function resolveLegacyDraftTimezone({ timezone = '', userTimezone = '', browserTimezone = '' } = {}) {
  return normalizeTimezone(timezone || userTimezone || browserTimezone || 'UTC', 'UTC');
}
